import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Camera,
  ChefHat,
  ChevronRight,
  Crown,
  Flame,
  MessageCircle,
  Target,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import telegramService from "../utils/telegram";
import apiService from "../services/api";
import { Badge } from "../types";
import { avatarInitial, avatarStyle } from "../utils/format";
import { useNavigation } from "../navigation/NavigationContext";
import { useApiAll } from "../hooks/useApi";
import GoalSetting from "./GoalSetting";
import Wizard from "./Wizard/Wizard";
import BadgeUnlockModal from "./BadgeUnlockModal";
import ReportCardDetail from "./ReportCardDetail";
import ShimmerSkeleton from "./ui/ShimmerSkeleton";
import ErrorState from "./ui/ErrorState";
import MacroCards from "./dashboard/MacroCards";
import TodayMeals from "./dashboard/TodayMeals";
import ReportCardTeaser from "./dashboard/ReportCardTeaser";

interface DashboardProps {
  onChromeHiddenChange?: (hidden: boolean) => void;
}

interface TodayFocusProps {
  totalCalories: number;
  dailyGoal: number;
  mealsCount: number;
  onLogFood: () => void;
}

const TodayFocusCard: React.FC<TodayFocusProps> = ({
  totalCalories,
  dailyGoal,
  mealsCount,
  onLogFood,
}) => {
  const { t } = useTranslation();
  const remaining = dailyGoal - totalCalories;
  const progress = dailyGoal > 0 ? Math.min(100, Math.max(0, (totalCalories / dailyGoal) * 100)) : 0;
  const isOver = remaining < 0;

  return (
    <motion.section
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      className="mx-4 rounded-[2rem] border border-line bg-surface p-5 shadow-card"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-content-tertiary">
            {t("home.today")}
          </p>
          <h2 className="mt-2 text-4xl font-black leading-none text-content-primary">
            {Math.abs(remaining)}
            <span className="ml-1 text-sm font-black text-content-tertiary">kcal</span>
          </h2>
          <p className={`mt-2 text-sm font-bold ${isOver ? "text-danger" : "text-success"}`}>
            {isOver ? t("home.overTarget") : t("home.leftForToday")}
          </p>
        </div>

        <button
          onClick={onLogFood}
          className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-content-primary text-content-inverse shadow-card active:scale-95 transition-transform"
          aria-label={t("quickActions.logFood")}
        >
          <Camera size={23} />
        </button>
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between text-xs font-bold text-content-secondary">
          <span>
            {totalCalories} / {dailyGoal} kcal
          </span>
          <span>{Math.round(progress)}%</span>
        </div>
        <div className="mt-2 h-3 overflow-hidden rounded-full bg-inset">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className={`h-full rounded-full ${isOver ? "bg-danger" : "bg-success"}`}
          />
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="rounded-2xl bg-inset px-3 py-3">
          <p className="text-[10px] font-bold uppercase tracking-wider text-content-tertiary">
            {t("home.eaten")}
          </p>
          <p className="mt-1 text-base font-black text-content-primary">{totalCalories} kcal</p>
        </div>
        <div className="rounded-2xl bg-inset px-3 py-3">
          <p className="text-[10px] font-bold uppercase tracking-wider text-content-tertiary">
            {t("home.meals")}
          </p>
          <p className="mt-1 text-base font-black text-content-primary">{mealsCount}</p>
        </div>
      </div>
    </motion.section>
  );
};

const CompactMomentum: React.FC<{
  currentStreak: number;
  level: number;
  onOpenCoach: () => void;
}> = ({ currentStreak, level, onOpenCoach }) => {
  const { t } = useTranslation();

  return (
    <div className="mx-4 grid grid-cols-2 gap-3">
      <div className="rounded-3xl border border-line bg-surface px-4 py-3 shadow-card">
        <div className="flex items-center gap-2 text-warning">
          <Flame size={18} />
          <span className="text-xs font-black">{t("gamification.streak")}</span>
        </div>
        <p className="mt-2 text-xl font-black text-content-primary">{currentStreak}</p>
        <p className="text-[11px] font-medium text-content-tertiary">
          {t("gamification.level", { level })}
        </p>
      </div>

      <button
        onClick={onOpenCoach}
        className="rounded-3xl border border-line bg-surface px-4 py-3 text-left shadow-card active:scale-[0.98] transition-transform"
      >
        <div className="flex items-center gap-2 text-info">
          <MessageCircle size={18} />
          <span className="text-xs font-black">{t("chatCoach.title")}</span>
        </div>
        <p className="mt-2 flex items-center gap-1 text-sm font-black text-content-primary">
          {t("home.askCoach")} <ChevronRight size={15} />
        </p>
      </button>
    </div>
  );
};

const NextBestAction: React.FC<{
  remainingCalories: number;
  onOpenRecipes: () => void;
}> = ({ remainingCalories, onOpenRecipes }) => {
  const { t } = useTranslation();
  const rangeMin = Math.max(250, Math.min(remainingCalories - 150, 500));
  const rangeMax = Math.max(rangeMin + 100, Math.min(remainingCalories, 750));

  return (
    <button
      onClick={onOpenRecipes}
      className="mx-4 flex items-center gap-3 rounded-3xl border border-brand-soft-2 bg-brand-soft px-4 py-4 text-left active:scale-[0.98] transition-transform"
    >
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-surface text-brand-text shadow-card">
        <ChefHat size={21} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-black text-content-primary">{t("home.nextMealIdea")}</p>
        <p className="mt-0.5 text-xs font-medium text-content-secondary">
          {t("home.nextMealRange", { min: rangeMin, max: rangeMax })}
        </p>
      </div>
      <ChevronRight size={18} className="shrink-0 text-brand-text" />
    </button>
  );
};

const Dashboard: React.FC<DashboardProps> = ({ onChromeHiddenChange }) => {
  const { t } = useTranslation();
  const { push } = useNavigation();
  const tgId = telegramService.getUserId();

  const [unseenBadge, setUnseenBadge] = useState<Badge | null>(null);
  const [showReportDetail, setShowReportDetail] = useState(false);
  const [showGoalSetting, setShowGoalSetting] = useState(false);
  const [wizardDone, setWizardDone] = useState(false);

  const { data, loading, error, refetch } = useApiAll(
    {
      user: () => apiService.getUser(tgId),
      stats: () => apiService.getTodayStats(tgId),
      meals: () => apiService.getTodayMeals(tgId),
      gamification: () => apiService.getGamification(tgId),
      subscription: () => apiService.getSubscription(tgId),
      reportCard: () => apiService.getReportCard(tgId),
    },
    [tgId],
    { enabled: Boolean(tgId) },
  );

  // 30 soniyada bir yangilash (bot orqali ovqat qo'shilsa ko'rinishi uchun)
  useEffect(() => {
    const interval = setInterval(refetch, 30000);
    return () => clearInterval(interval);
  }, [refetch]);

  // Ko'rilmagan badge modalini ochish
  useEffect(() => {
    const unseen = data?.gamification?.unseenBadges;
    if (unseen && unseen.length > 0) setUnseenBadge(unseen[0]);
  }, [data?.gamification]);

  useEffect(() => {
    return () => onChromeHiddenChange?.(false);
  }, [onChromeHiddenChange]);

  const user = data?.user ?? null;
  const stats = data?.stats ?? null;
  const needsOnboarding = Boolean(user && !wizardDone && (!user.gender || !user.goal));

  useEffect(() => {
    onChromeHiddenChange?.(needsOnboarding);
  }, [needsOnboarding, onChromeHiddenChange]);

  const handleBadgeDismiss = async () => {
    if (!unseenBadge) return;
    if (tgId) {
      await apiService.markBadgesSeen(tgId, [unseenBadge.id]).catch(() => {});
    }
    const remaining =
      data?.gamification?.unseenBadges.filter((b) => b.id !== unseenBadge.id) || [];
    setUnseenBadge(remaining.length > 0 ? remaining[0] : null);
  };

  if (loading && !data) return <ShimmerSkeleton variant="dashboard" />;

  if (error || !user || !stats) {
    return <ErrorState onRetry={refetch} />;
  }

  if (needsOnboarding) {
    return (
      <Wizard
        user={user}
        onComplete={() => {
          setWizardDone(true);
          refetch();
        }}
      />
    );
  }

  if (showGoalSetting) {
    return (
      <div className="p-4">
        <GoalSetting
          currentGoal={user.dailyGoal}
          onGoalSet={() => {
            setShowGoalSetting(false);
            refetch();
          }}
        />
      </div>
    );
  }

  const gamification = data?.gamification ?? null;
  const isPremium = data?.subscription?.isPremium || false;
  const reportCard = data?.reportCard ?? null;
  const meals = data?.meals ?? [];

  const handleLogFood = () => {
    telegramService.haptic("medium");
    if (tgId) {
      apiService.promptLog(tgId).catch(() => {});
    }
    telegramService.close();
  };

  return (
    <div className="mx-auto min-h-screen w-full max-w-md pb-32 space-y-5 relative overflow-x-hidden bg-app">
      <div className="px-5 flex items-center justify-between sticky top-0 z-10 backdrop-blur-md bg-surface-elevated pb-3 pt-[calc(1.25rem+var(--safe-top))] border-b border-line">
        <div>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-3"
          >
            <div className="relative">
              <div
                className="avatar w-11 h-11 text-base ring-2 ring-surface shadow-card"
                style={avatarStyle(user.tgId || "0")}
              >
                {avatarInitial(user.firstName)}
              </div>
              {isPremium && (
                <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full gradient-warm flex items-center justify-center shadow-soft border-2 border-surface">
                  <Crown size={10} className="text-white" />
                </div>
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-black text-content-primary">
                  {t("home.hello")}, {user.firstName}
                </h1>
                {isPremium && (
                  <span className="text-[10px] font-black px-1.5 py-0.5 rounded-md gradient-warm text-white shadow-soft">
                    PRO
                  </span>
                )}
              </div>
              <p className="text-[12px] text-content-tertiary font-medium">
                {t("home.todayFocus")}
              </p>
            </div>
          </motion.div>
        </div>
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={() => {
            telegramService.haptic("light");
            setShowGoalSetting(true);
          }}
          className="w-11 h-11 bg-surface rounded-2xl shadow-card border border-line text-content-primary flex items-center justify-center"
        >
          <Target size={20} />
        </motion.button>
      </div>

      <TodayFocusCard
        totalCalories={stats.totalCalories}
        dailyGoal={stats.dailyGoal}
        mealsCount={stats.mealsCount}
        onLogFood={handleLogFood}
      />

      <MacroCards stats={stats} />

      {gamification && (
        <CompactMomentum
          currentStreak={gamification.currentStreak}
          level={gamification.level}
          onOpenCoach={() => push("chat_coach")}
        />
      )}

      <NextBestAction
        remainingCalories={stats.remainingCalories}
        onOpenRecipes={() => push("recipes")}
      />

      {/* Today's Meals */}
      <TodayMeals meals={meals} onChanged={refetch} />

      {/* Daily Report Card */}
      {reportCard && (
        <ReportCardTeaser report={reportCard} onOpen={() => setShowReportDetail(true)} />
      )}

      {/* Report Card Detail Modal */}
      <ReportCardDetail
        report={reportCard}
        isOpen={showReportDetail}
        onClose={() => setShowReportDetail(false)}
        onUpgrade={() => {
          setShowReportDetail(false);
          push("premium");
        }}
      />

      {/* Badge Unlock Modal */}
      <BadgeUnlockModal badge={unseenBadge} onClose={handleBadgeDismiss} />
    </div>
  );
};

export default Dashboard;
